import { IQAccess, RoleWithPermissions } from "@/hooks/useRbac";
import jwt_decode from "jwt-decode";
import { FEATURE_PERMISSION_MAPPING, PermissionKey, SARAS_ROLES_MAPPING } from "@/constants/rbac";
import { IS_LOCAL_DEV, isLocalDevEndUserPersona } from "@/config/localDev";
import { ADMIN_ROLES } from "@/constants";

export const getRolesFromToken = (accessToken: any) => {
  if(!accessToken){
    return;
  }
  const jwtPayload: any = jwt_decode(accessToken);
  if (jwtPayload && jwtPayload.saras_claims) {
    return jwtPayload.saras_claims;
  } else {
    // invalid JWT token
    return null;
  }
};

export const getUsetIdFromToken = (accessToken: any) => {
  const jwtPayload: any = jwt_decode(accessToken);
  if (jwtPayload && jwtPayload.user_id) {
    return jwtPayload.user_id;
  } else {
    // invalid JWT token
    return null;
  }
};

/* function to check if token has expired or not */
export const isTokenExpired = (accessToken: any) => {
  const jwtPayload: any = jwt_decode(accessToken);
  if (jwtPayload && jwtPayload.exp) {
    return jwtPayload.exp < Date.now() / 1000;
  } else {
    // invalid JWT token
    return true;
  }
};

export const hasRoleInToken = (accessToken: any, role: string | string[]) => {
  // Local-dev mode has no real JWT, so grant every role — this is the single
  // chokepoint for token-based role gates (dashboard create, godview, adoption
  // metrics), mirroring the all-permissions grant in LOCAL_DEV_ROLE_ACCESS.
  // Exception: the dev-only end-user persona override resolves every creator/admin
  // role check to false (end users carry no token roles), so localhost can render
  // the consumer persona. Inert outside IS_LOCAL_DEV — see localDev.ts.
  if (IS_LOCAL_DEV) {
    return !isLocalDevEndUserPersona();
  }
  const roles = getRolesFromToken(accessToken);
  if (roles) {
    if (Array.isArray(role)) {
      return role.some(r => roles.includes(r));
    } else {
      return roles.includes(role);
    }
  } else {
    // invalid JWT token
    return false;
  }
};

export const hasAppPermision = (accessToken: any, permissions: RoleWithPermissions[]): IQAccess => {
  const iqAccess: IQAccess = {
    APP_PERMISSION: false,
    READ_WAREHOUSE: false,
    WRITE_WAREHOUSE: false,
    READ_SUBSCRIPTION: false,
    WRITE_SUBSCRIPTION: false,
    READ_THREADS: false,
    WRITE_THREADS: false,
    READ_USERMANAGEMENT: false,
    WRITE_USERMANAGEMENT: false,
    READ_GODVIEW: false,
    READ_ADMINFEEDBACKLIST: false,
    READ_ADOPTION_METRICS: false,
    READ_INTEGRATIONS: false,
    WRITE_INTEGRATIONS: false,
    READ_DASHBOARD: false,
    WRITE_DASHBOARD: false,
    READ_USECASE: false,
    WRITE_USECASE: false,
    SHARE_ANY_COMPANY: false,
    SHARE_OWN_ORG: false,
    READ_CONTEXT_CONSOLE: false,
    READ_DASHBOARD_PROMPTS: false,
    VIEW_INTEGRATION_STATUS: false,
    MANAGE_INTEGRATIONS: false,
  }
  const roles = getRolesFromToken(accessToken);
  if(!!roles?.length && !!permissions?.length){
    // 1. Filter the list of all roles to only include the ones the user has in their token.
    const userRolesWithPermissions = permissions.filter(p => roles.includes(p.name));

    // 2. Flatten all permission names from the user's roles into a single array of strings.
    const allPermissionNames = userRolesWithPermissions.flatMap(role => 
      role.permissions?.map(p => p.name) || []
    );

    // 3. Remove duplicates to get a clean list of unique permissions the user has.
    const uniqueUserPermissions = [...new Set(allPermissionNames)];

    // 4. Iterate over the defined permission keys and check if the user has the required permission.
    for (const key in FEATURE_PERMISSION_MAPPING) {
      const permissionKey = key as PermissionKey;
      const requiredPermission = FEATURE_PERMISSION_MAPPING[permissionKey];

      if (Array.isArray(requiredPermission)) {
        if (requiredPermission.some((p) => uniqueUserPermissions.includes(p))) {
          iqAccess[permissionKey] = true;
        }
      } else if (uniqueUserPermissions.includes(requiredPermission)) {
        iqAccess[permissionKey] = true;
      }
    }

    // Transition safety net: both admin roles (SarasAdmin, IQDashboardAdmin) get the
    // full elevation set regardless of what user-service has provisioned for them.
    // The primary path is capability resolution from /role/all above — remove this
    // bypass once both roles' provisioning is verified per environment.
    // Deliberate consequence: this grants IQDashboardAdmin EVERY SARAS_ROLES_MAPPING
    // key — including READ_GODVIEW / READ_ADMINFEEDBACKLIST / READ_ADOPTION_METRICS —
    // mirroring the backend's ADMIN_ROLES (get_admin_user) blanket elevation; flagged
    // for product confirmation.
    if (hasRoleInToken(accessToken, ADMIN_ROLES)) {
      Object.keys(SARAS_ROLES_MAPPING).forEach(key => {
        iqAccess[key as PermissionKey] = true;
      });
    }
  }
  return iqAccess;
}